import { Link } from "react-router-dom";
import { useBranding, brandLabel } from "@/context/BrandingContext";

/**
 * Logo — monogramme + wordmark, personnalisable par boutique (Studio de
 * thème > Identité de marque). Nom et tagline viennent de useBranding() ;
 * repli sur "ClickExpert" / "Solution web et mobile" (contenu d'origine) tant que rien
 * n'est personnalisé ou que l'appel réseau n'a pas répondu. Si la boutique a
 * uploadé son propre logo (logo_url), l'image remplace le monogramme généré,
 * mais le wordmark (nom + tagline selon les props ci-dessous) reste affiché
 * à côté dans les deux cas — une image de logo ne porte pas forcément déjà
 * le nom de la boutique (ex: un simple symbole/icône sans texte).
 *
 * Props:
 *  - variant : "dark" (défaut) pour fond clair (header cream) — texte ink
 *              "light" pour fond sombre (footer ink) — texte cream
 *  - size    : "sm" | "md" | "lg" — hauteur du monogramme
 *  - showBrandingTagline : booléen (def. true) — affiche branding.tagline
 *              (Studio de thème > Identité) sous le wordmark. À FAUX dans
 *              l'en-tête (Header.jsx) : sa hauteur est fixe (bandeau collant
 *              d'une seule ligne, voir Layout.jsx qui réserve un padding-top
 *              en pixels calé dessus) — une tagline personnalisée, de
 *              longueur libre, y ferait déborder le logo et chevaucherait le
 *              contenu de la page. Sans contrainte de hauteur ailleurs
 *              (pied de page, écrans de connexion), elle reste affichée.
 *  - to      : lien (par défaut "/") ; passer null pour rendre non cliquable
 *  - className, testId
 */
const SIZE_MAP = {
  sm: { mono: "h-8 md:h-9", title: "text-[13px] md:text-sm", sub: "text-[10px] md:text-[11px]", tag: "text-[9px] md:text-[10px]" },
  md: { mono: "h-10 md:h-12", title: "text-[15px] md:text-lg", sub: "text-[11px] md:text-xs", tag: "text-[10px] md:text-[11px]" },
  lg: { mono: "h-14 md:h-16", title: "text-lg md:text-2xl", sub: "text-xs md:text-sm", tag: "text-[11px] md:text-xs" },
};

// Symbole de marque ClickExpert — « un point qui devient chevron : le clic,
// puis l'élan » (kit de logo, voir frontend/public/brand/README.md).
//
// Redessiné ici en JSX plutôt que chargé depuis public/brand/symbole.svg :
// une <img> ne peut pas être recolorée, or un thème de boutique a besoin de
// poser ce symbole dans SA palette (voir les props `bg`/`mark`, utilisées
// par l'en-tête du moteur "agence_v1"). Le tracé est identique à celui du
// kit — toute retouche doit être reportée aux deux endroits.
//
// `bg` : le carré arrondi. `mark` : le point et le chevron.
export const LogoMonogram = ({ className = "", bg = "#2C63DF", mark = "#FFFFFF" }) => (
  <svg
    viewBox="0 0 32 32"
    xmlns="http://www.w3.org/2000/svg"
    className={className}
    aria-hidden="true"
    focusable="false"
  >
    <rect width="32" height="32" rx="8" fill={bg} />
    <circle cx="9.5" cy="16" r="3.1" fill={mark} />
    <path
      d="M16.5 9.5 L23 16 L16.5 22.5"
      fill="none"
      stroke={mark}
      strokeWidth="3.4"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const Wordmark = ({ variant = "dark", sizes, showBrandingTagline = true, name, tagline, loaded = true }) => {
  const textColor = variant === "light" ? "text-cream" : "text-ink";
  const subColor = variant === "light" ? "text-cream/70" : "text-graphite";
  // Fondu 300 ms quand loaded passe à true — évite le snap brutal du nom
  // de boutique (invisible → visible). Le nœud DOM reste monté (layout
  // stable, pas de saut de hauteur), seule l'opacité transite.
  return (
    <span className={`flex flex-col leading-none min-w-0 transition-opacity duration-300 ${loaded ? "opacity-100" : "opacity-0"}`}>
      <span
        className={`font-sans font-semibold uppercase tracking-[0.14em] ${sizes.title} ${textColor} break-words`}
      >
        {name}
      </span>
      {showBrandingTagline && tagline && (
        <span
          className={`font-sans font-medium uppercase tracking-[0.32em] ${sizes.sub} ${subColor} mt-1 break-words`}
        >
          {tagline}
        </span>
      )}
    </span>
  );
};

export const Logo = ({
  variant = "dark",
  size = "md",
  showBrandingTagline = true,
  to = "/",
  className = "",
  testId = "logo-link",
  compact = false, // en mobile compact : n'affiche que le monogramme
}) => {
  const branding = useBranding();
  const sizes = SIZE_MAP[size] ?? SIZE_MAP.md;
  // Logo uploadé par la boutique (image) ou monogramme généré à défaut —
  // dans les deux cas, le wordmark (nom + tagline le cas échéant) reste
  // affiché à côté : une image de logo ne porte pas nécessairement déjà le
  // nom de la boutique (ex: un simple symbole/icône), et le masquer
  // laissait alors le header sans aucune trace textuelle du nom.
  const content = (
    <span className={`inline-flex items-center gap-2.5 md:gap-3 min-w-0 ${className}`}>
      {branding.logo_url ? (
        <img
          src={branding.logo_url}
          alt={branding.name}
          className={`${sizes.mono} w-auto shrink-0 object-contain`}
        />
      ) : branding.loaded ? (
        <LogoMonogram className={`${sizes.mono} w-auto shrink-0`} />
      ) : (
        // Identité pas encore connue : un espace de la taille du monogramme,
        // jamais le symbole ClickExpert d'une boutique qui n'est pas la sienne.
        <span className={`${sizes.mono} aspect-square shrink-0`} aria-hidden="true" />
      )}
      {/* Wordmark : caché sur très petit écran si compact, sinon toujours
          visible. min-w-0 partout dans cette chaîne (voir Wordmark) — sans
          ça, un texte long (nom de boutique ou tagline) déborde du logo au
          lieu de passer à la ligne dans son propre espace. Texte réglé dans
          le Studio (brandLabel), ou rien du tout si la boutique a choisi
          « logo seul ». */}
      {!branding.hide_name && (
        <span className={`min-w-0 ${compact ? "hidden xs:inline-flex sm:inline-flex" : "inline-flex"}`}>
          <Wordmark variant={variant} sizes={sizes} showBrandingTagline={showBrandingTagline} name={brandLabel(branding)} tagline={branding.tagline} loaded={branding.loaded} />
        </span>
      )}
    </span>
  );

  if (!to) {
    return (
      <span data-testid={testId} className="select-none">
        {content}
      </span>
    );
  }

  return (
    <Link
      to={to}
      data-testid={testId}
      className="select-none inline-flex items-center hover:opacity-90 transition-opacity"
      aria-label={`${branding.name} — accueil`}
    >
      {content}
    </Link>
  );
};

export default Logo;
